/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * An upstream remote_session held for the same subject and issuer as the user_session that carries it. Token material is never returned.
 */
export type UserSessionUpstream = {
  /**
   * Upstream access-token expiry. Null when the upstream issued a non-expiring token.
   */
  accessExpiresAt?: Date | undefined;
  /**
   * Absolute upstream authorization deadline. Unlike refresh_expires_at, exchanging a token does not extend this.
   */
  authorizationExpiresAt?: Date | undefined;
  /**
   * Whether the subject opted this connection into automated keepalive.
   */
  autoRefresh: boolean;
  /**
   * Whether a refresh grant is held. Gates 'refresh now'; refresh_expires_at is insufficient because an upstream may issue a non-expiring refresh token.
   */
  hasRefreshToken: boolean;
  /**
   * Display slug of the upstream provider, e.g. 'mcp.linear.app'.
   */
  issuerSlug: string;
  /**
   * When this upstream token was last spent on a proxied call. Same five-minute resolution as the inbound leg, so the two are directly comparable.
   */
  lastUsedAt?: Date | undefined;
  /**
   * Upstream refresh-token expiry. Null when the session holds no refresh token or the upstream issued a non-expiring one.
   */
  refreshExpiresAt?: Date | undefined;
  /**
   * The remote_session_client the session was minted against.
   */
  remoteSessionClientId: string;
  /**
   * The remote_session id. Target for revoke and force-refresh.
   */
  remoteSessionId: string;
  /**
   * The remote_session_issuer the client belongs to.
   */
  remoteSessionIssuerId: string;
  /**
   * Scopes held by this upstream session.
   */
  scopes: Array<string>;
};

/** @internal */
export const UserSessionUpstream$inboundSchema: z.ZodMiniType<
  UserSessionUpstream,
  unknown
> = z.pipe(
  z.object({
    access_expires_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    authorization_expires_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    auto_refresh: z.boolean(),
    has_refresh_token: z.boolean(),
    issuer_slug: z.string(),
    last_used_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    refresh_expires_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    remote_session_client_id: z.string(),
    remote_session_id: z.string(),
    remote_session_issuer_id: z.string(),
    scopes: z.array(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      "access_expires_at": "accessExpiresAt",
      "authorization_expires_at": "authorizationExpiresAt",
      "auto_refresh": "autoRefresh",
      "has_refresh_token": "hasRefreshToken",
      "issuer_slug": "issuerSlug",
      "last_used_at": "lastUsedAt",
      "refresh_expires_at": "refreshExpiresAt",
      "remote_session_client_id": "remoteSessionClientId",
      "remote_session_id": "remoteSessionId",
      "remote_session_issuer_id": "remoteSessionIssuerId",
    });
  }),
);

export function userSessionUpstreamFromJSON(
  jsonString: string,
): SafeParseResult<UserSessionUpstream, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => UserSessionUpstream$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'UserSessionUpstream' from JSON`,
  );
}
